/**
 * SIL-1348 - the right-column action card on the buyer listing detail (spec
 * section 4.2, handoff section 2 "right column").
 *
 * The tier decides the whole card, and the tier is the SERVER's:
 *
 *  - **Verified** — the exact list price, the delta badge, the per-node /
 *    quantity / lot-total rows, and two buttons: `Buy now` (the ONLY filled
 *    button on the page) and `Make an offer` (outline).
 *  - **Unverified** — the bucketed range, the same rows as ranges, the "Unlock
 *    the exact price" block with `Get verified`, and the note. No exact price
 *    reaches this component at all: `UnverifiedPricing` has no field for one.
 *
 * ## Two absences, both deliberate
 *
 *  1. **No "Express interest".** Slice 4 owns it; until then the string must not
 *     appear for either tier, so there is no hidden branch and no label in the
 *     copy module to accidentally render.
 *  2. **No `minOfferUsd`.** It is seller-private and the key is omitted from
 *     every buyer payload, verified included (SIL-1345). This component never
 *     receives it, and nothing here should start asking for it.
 *
 * `Buy now` calls the caller's `onBuyNow` — SIL-1427 (spec section 5) wires it
 * to the real Buy now modal / purchase request, replacing the SIL-1318
 * interim rule where this button opened the Make-an-offer dialog instead.
 * When `purchasePending` is true (another open buy_now request already
 * exists on this listing — the server's own mirror of
 * `offer_one_open_buy_now_per_listing`, migration 972), the button is
 * disabled with a "Purchase pending" caption while `Make an offer` stays
 * enabled: the second buyer can still negotiate even though Buy now is
 * spoken for.
 */
import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import type { BuyerPricing } from './listingShape.js';
import { Card } from '../Card.js';
import { Button } from '../Button.js';
import { cn } from '../utils.js';
import { listingPriceView, PER_GPU_UNKNOWN } from './listingPriceView.js';
import { BUYER_VERIFY_BANNER_CTA } from './copy/buyerVerification';
import {
  ACTION_BUY_NOW,
  ACTION_LIST_PRICE_EYEBROW,
  ACTION_MAKE_OFFER,
  ACTION_OFFERS_CLOSED,
  ACTION_OFFER_EXISTS,
  ACTION_PER_GPU_SUFFIX,
  ACTION_PER_NODE_SUFFIX,
  ACTION_PROFILE_HREF,
  ACTION_PROFILE_INCOMPLETE_LEAD,
  ACTION_PROFILE_INCOMPLETE_LINK,
  ACTION_PROFILE_INCOMPLETE_TAIL,
  ACTION_PURCHASE_PENDING_CAPTION,
  ACTION_RANGE_EYEBROW,
  ACTION_ROW_LABELS,
  ACTION_UNLOCK_BODY,
  ACTION_UNLOCK_HEADING,
  ACTION_UNVERIFIED_NOTE,
  ACTION_VERIFY_TO_OFFER_BODY,
  ACTION_VERIFY_TO_OFFER_HEADING,
  actionQuantityValue,
  pctVsFmvLabel,
} from './copy/listingDetail';

function Eyebrow({ children }: { children: string }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-num text-sm text-foreground">{value}</span>
    </div>
  );
}

/** `pctVsFmv` below FMV median is good news for a buyer, hence the success tone. */
function DeltaBadge({ pct }: { pct: number }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        pct < 0
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : 'bg-muted text-muted-foreground ring-border',
      )}
    >
      {pctVsFmvLabel(pct)}
    </span>
  );
}

/** SIL-2217: why `canTransact` is false; picks the note shown in place of the buttons. */
export type OffersBlockedReason = 'closed' | 'profile_incomplete' | 'existing_offer';

function PerGpuHeadline({ perGpu, perNode }: { perGpu: string; perNode: string }) {
  // SIL-2217: no GPU count/price -> lead with the per-node price, never "n/a / GPU".
  const noGpu = perGpu === PER_GPU_UNKNOWN;
  return (
    <p className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
      <span className="font-num text-2xl font-semibold text-foreground">
        {noGpu ? perNode : perGpu}
      </span>
      <span className="text-xs text-muted-foreground">
        {noGpu ? ACTION_PER_NODE_SUFFIX : ACTION_PER_GPU_SUFFIX}
      </span>
    </p>
  );
}

function OffersBlockedNote({
  reason,
  renderProfileLink,
}: {
  reason: OffersBlockedReason;
  renderProfileLink?: (label: string) => ReactNode;
}) {
  if (reason === 'profile_incomplete') {
    return (
      <p className="mt-4 text-xs text-muted-foreground" data-testid="offers-blocked-profile">
        {ACTION_PROFILE_INCOMPLETE_LEAD}{' '}
        {renderProfileLink ? (
          renderProfileLink(ACTION_PROFILE_INCOMPLETE_LINK)
        ) : (
          <a href={ACTION_PROFILE_HREF} className="font-medium text-brand-600 hover:underline">
            {ACTION_PROFILE_INCOMPLETE_LINK}
          </a>
        )}{' '}
        {ACTION_PROFILE_INCOMPLETE_TAIL}
      </p>
    );
  }
  return (
    <p className="mt-4 text-xs text-muted-foreground">
      {reason === 'existing_offer' ? ACTION_OFFER_EXISTS : ACTION_OFFERS_CLOSED}
    </p>
  );
}

export interface ActionCardProps {
  /** SIL-1480: `AskPricing` ('ask' mode, arbitrage listings) shows the single
   * exact asking price to EVERY caller, no range and no pctVsFmv — see the
   * `askMode` branch below. */
  pricing: BuyerPricing;
  /** Nodes behind the listing — the "Quantity" row. */
  nodeCount: number;
  /**
   * Whether the API would accept an offer right now (`acceptingOffers`, which
   * mirrors the route's own gate) AND the caller has no live offer already.
   * False hides both buttons and shows the closed note instead of rendering a
   * control the API would 409.
   */
  canTransact: boolean;
  /** SIL-2217: cause of `canTransact === false`; defaults to 'closed'. */
  offersBlockedReason?: OffersBlockedReason;
  /** SIL-2217: router-aware link to `/profile#buying` (plain anchor if omitted). */
  renderProfileLink?: (label: string) => ReactNode;
  /**
   * SIL-1427: another open buy_now request already exists on this listing
   * (`listing.purchasePending`, server-mirrored). Disables ONLY the Buy now
   * button — Make an offer stays enabled so a second buyer can still
   * negotiate.
   */
  purchasePending: boolean;
  /**
   * SIL-1472: `listing.offersOnly` — Buy now is never shown for this
   * listing. A band listing's exact price is withheld from every
   * non-owner (even a verified buyer), which lands `pricing.tier` on
   * 'unverified' here regardless of the caller's actual verification —
   * `buyerVerified` below is what decides whether THIS card offers Make an
   * offer directly or asks the buyer to verify first.
   */
  offersOnly: boolean;
  /** The caller's ACTUAL buyer-verification status (SIL-1326 still gates
   * every offer server-side) — independent of `pricing.tier`, which a band
   * listing deliberately forces to 'unverified' for non-owners. */
  buyerVerified: boolean;
  onBuyNow: () => void;
  onMakeOffer: () => void;
  onGetVerified: () => void;
}

export function ActionCard({
  pricing,
  nodeCount,
  canTransact,
  offersBlockedReason = 'closed',
  renderProfileLink,
  purchasePending,
  offersOnly,
  buyerVerified,
  onBuyNow,
  onMakeOffer,
  onGetVerified,
}: ActionCardProps) {
  const view = listingPriceView(pricing, undefined, { unitCount: nodeCount });
  const verified = view.mode === 'verified';
  // SIL-1480: 'ask' mode (arbitrage listings) — one exact price to EVERY
  // caller, no FMV delta, same ring treatment as verified (an exact price,
  // not a range) but its own button logic below (offers-only, mirroring the
  // offersOnly branch other listings use).
  const askMode = view.mode === 'ask';

  return (
    <Card
      as="section"
      className={cn((verified || askMode) && 'ring-2 ring-brand-600')}
      data-testid="listing-action-card"
    >
      <Eyebrow>{verified || askMode ? ACTION_LIST_PRICE_EYEBROW : ACTION_RANGE_EYEBROW}</Eyebrow>

      {view.mode === 'ask' ? (
        <>
          <p className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
            <span className="font-num text-2xl font-semibold text-foreground" data-testid="ask-price">
              {view.headline}
            </span>
          </p>
          <div className="mt-4 divide-y divide-border border-y border-border">
            <Row label={ACTION_ROW_LABELS.quantity} value={actionQuantityValue(nodeCount)} />
            <Row label={ACTION_ROW_LABELS.lotTotal} value={view.total} />
          </div>
          {/* SIL-1480: arbitrage ('ask' mode) listings are offers-only by
              construction (982's INSERT always sets offers_only=true) —
              Make an offer, gated on the caller's ACTUAL verification,
              exactly like the offersOnly branch below. No "unlock the exact
              price" block: the exact price is already shown above, to
              everyone. */}
          {buyerVerified ? (
            canTransact ? (
              <div className="mt-4">
                <Button variant="primary" className="w-full" onClick={onMakeOffer}>
                  {ACTION_MAKE_OFFER}
                </Button>
              </div>
            ) : (
              <OffersBlockedNote reason={offersBlockedReason} renderProfileLink={renderProfileLink} />
            )
          ) : (
            <div className="mt-4 rounded-[9px] bg-brand-600 p-4 shadow-md">
              <p className="text-sm font-semibold text-white">{ACTION_VERIFY_TO_OFFER_HEADING}</p>
              <p className="mt-1 text-xs text-white/85">{ACTION_VERIFY_TO_OFFER_BODY}</p>
              <Button variant="secondary" size="sm" className="mt-3 w-full gap-1.5" onClick={onGetVerified}>
                {BUYER_VERIFY_BANNER_CTA}
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Button>
            </div>
          )}
        </>
      ) : view.mode === 'verified' ? (
        <>
          <PerGpuHeadline perGpu={view.perGpu} perNode={view.perNode} />
          {view.pctVsFmv !== null && (
            <p className="mt-2">
              <DeltaBadge pct={view.pctVsFmv} />
            </p>
          )}
          <div className="mt-4 divide-y divide-border border-y border-border">
            <Row label={ACTION_ROW_LABELS.perNode} value={view.perNode} />
            <Row label={ACTION_ROW_LABELS.quantity} value={actionQuantityValue(nodeCount)} />
            <Row label={ACTION_ROW_LABELS.lotTotal} value={view.total} />
          </div>
          {canTransact ? (
            <div className="mt-4 space-y-2">
              {/* The ONE filled button on the page (handoff: Von Restorff). Any
                  second `variant="primary"` here is the bug the test guards. */}
              <Button
                variant="primary"
                className="w-full"
                onClick={onBuyNow}
                disabled={purchasePending}
              >
                {ACTION_BUY_NOW}
              </Button>
              {purchasePending && (
                <p className="text-center text-xs text-muted-foreground">
                  {ACTION_PURCHASE_PENDING_CAPTION}
                </p>
              )}
              <Button variant="secondary" className="w-full" onClick={onMakeOffer}>
                {ACTION_MAKE_OFFER}
              </Button>
            </div>
          ) : (
            <OffersBlockedNote reason={offersBlockedReason} renderProfileLink={renderProfileLink} />
          )}
        </>
      ) : (
        <>
          <PerGpuHeadline perGpu={view.perGpu} perNode={view.perNode} />
          <div className="mt-4 divide-y divide-border border-y border-border">
            <Row label={ACTION_ROW_LABELS.perNode} value={view.perNode} />
            <Row label={ACTION_ROW_LABELS.quantity} value={actionQuantityValue(nodeCount)} />
            <Row label={ACTION_ROW_LABELS.lotTotal} value={view.total} />
          </div>
          {/* SIL-1472: an offers-only listing never shows Buy now and never
              unlocks an exact price for anyone but the owner — a band
              listing shows the SAME band to a verified non-owner too. Once
              the caller is an actual verified buyer, Make an offer is
              available directly; otherwise verification is still required
              to offer at all (SIL-1326), same as any other listing. */}
          {offersOnly ? (
            buyerVerified ? (
              canTransact ? (
                <div className="mt-4">
                  <Button variant="primary" className="w-full" onClick={onMakeOffer}>
                    {ACTION_MAKE_OFFER}
                  </Button>
                </div>
              ) : (
                <OffersBlockedNote reason={offersBlockedReason} renderProfileLink={renderProfileLink} />
              )
            ) : (
              <div className="mt-4 rounded-[9px] bg-brand-600 p-4 shadow-md">
                <p className="text-sm font-semibold text-white">{ACTION_VERIFY_TO_OFFER_HEADING}</p>
                <p className="mt-1 text-xs text-white/85">{ACTION_VERIFY_TO_OFFER_BODY}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3 w-full gap-1.5"
                  onClick={onGetVerified}
                >
                  {BUYER_VERIFY_BANNER_CTA}
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Button>
              </div>
            )
          ) : (
            <>
              <div className="mt-4 rounded-[9px] bg-brand-600 p-4 shadow-md">
                <p className="text-sm font-semibold text-white">{ACTION_UNLOCK_HEADING}</p>
                <p className="mt-1 text-xs text-white/85">{ACTION_UNLOCK_BODY}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3 w-full gap-1.5"
                  onClick={onGetVerified}
                >
                  {BUYER_VERIFY_BANNER_CTA}
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Button>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">{ACTION_UNVERIFIED_NOTE}</p>
            </>
          )}
        </>
      )}
    </Card>
  );
}
